import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { isB2B } from '@/core/settings/utils';

import type { ListingPage } from '../model';
import { getPageInfo } from '../services/route';
import { useInjectListingState } from '../stateInjection/hooks/useInjectListingState';
import { useUrl } from './useUrl';

let startWithInitialSearch = false;

export const useListing = (
  promisedSaga: () => Promise<() => Generator<unknown, any, unknown>>,
  pageName: ListingPage,
): void => {
  const router = useRouter();
  const dispatch = useDispatch();
  const url = useUrl();
  const { injectionState, requestState } = useInjectListingState(
    promisedSaga,
    pageName,
  );

  useEffect(() => {
    function handleRouteChange(nextUrl: string): void {
      if (injectionState === 'idle') {
        const pageInfo = getPageInfo(nextUrl);
        if (pageInfo && pageName === pageInfo.name) {
          const previousBaseUrl = url?.split('?')[0];
          const nextBaseUrl = nextUrl.split('?')[0];

          if (previousBaseUrl === nextBaseUrl) {
            startWithInitialSearch = true;
            requestState();
          }
        }
      }
    }

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [injectionState, pageName, requestState, router.events, url]);

  useEffect(() => {
    if (injectionState === 'ready') {
      import('../actions').then(({ startListing }) => {
        dispatch(startListing(startWithInitialSearch));
      });
    }
  }, [dispatch, injectionState]);

  useEffect(
    () => () => {
      import('../actions').then(({ stopListing }) => {
        dispatch(stopListing());
      });
    },
    [dispatch],
  );

  useEffect(() => {
    if (isB2B() && injectionState === 'idle') {
      requestState();
      startWithInitialSearch = true;
    }
  }, [requestState, injectionState]);
};
